import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, ChevronRight, ChevronDown, Zap } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { useServiceBySlug, usePageContent } from "@/lib/content";
import { StarMotif } from "@/components/common/StarMotif";
import { CTABlock } from "@/components/common/CTABlock";
import { absUrl } from "@/components/admin/UploadWidget";

// Static card config for the listing page — defines display title, description, CTA text, and slug for routing.
const SERVICE_CARDS = [
    // Tier 1 — Core Development & AI Engine
    {
        slug: "product-engineering",
        title: "Product Engineering",
        subtitle: "Custom Application & Product Development",
        description: "Build, modernize, and scale enterprise digital products with dedicated RKS Systems engineering pods.",
        cta: "Explore Engineering Capabilities",
    },
    {
        slug: "intelligent-automation",
        title: "Agentic AI",
        subtitle: "Autonomous Business Process Automation",
        description: "Deploy self-learning AI agents that automate complex workflows, invoice processing, and operational tasks.",
        cta: "See Agentic AI Use Cases",
    },
    {
        slug: "data-ai",
        title: "Data & AI",
        subtitle: "Enterprise Data & Generative AI Solutions",
        description: "Turn raw enterprise data into production-ready predictive AI models, custom LLMs, and real-time intelligence platforms.",
        cta: "View AI & Data Architectures",
    },
    {
        slug: "analytics",
        title: "Analytics",
        subtitle: "Real-Time Business Intelligence & Insights",
        description: "Drive confident enterprise decision-making with automated reporting, telemetry, and scalable data pipelines.",
        cta: "Explore Analytics Services",
    },
    // Tier 2 — Platform & Infrastructure
    {
        slug: "cloud-devops",
        title: "Cloud & DevOps",
        subtitle: "Cloud-Native Modernization & FinOps",
        description: "Secure, scalable AWS and cloud infrastructure with automated CI/CD pipelines and optimized cost control.",
        cta: "See Cloud Modernization",
    },
    {
        slug: "testing",
        title: "Quality Engineering",
        subtitle: "Automated Testing & Continuous Verification",
        description: "Embed quality and security performance testing into every sprint release for zero-defect software deployments.",
        cta: "Explore QA Testing Practices",
    },
    {
        slug: "security",
        title: "Security",
        subtitle: "DevSecOps & Enterprise Security Controls",
        description: "Comprehensive security engineered directly into every software layer — from architecture to deployment.",
        cta: "View Security Protocols",
    },
];

function ServiceCard({ s }) {
    return (
        <Link to={`/what-we-do/${s.slug}`} data-testid={`services-card-${s.slug}`}
            className="group relative overflow-hidden rounded-xl border border-border bg-card p-7 transition-all hover:border-primary/40 hover:shadow-lg hover:-translate-y-1 flex flex-col">
            <div className="absolute -right-6 -top-6 h-24 w-24 opacity-[0.06] group-hover:opacity-[0.14] transition-opacity">
                <StarMotif className="h-full w-full text-primary" label="RKS" labelColor="currentColor" />
            </div>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-secondary text-primary mb-4 flex-shrink-0">
                <StarMotif className="h-5 w-5" />
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">{s.subtitle}</div>
            <h3 className="font-heading text-xl font-bold">{s.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{s.description}</p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                {s.cta} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </div>
        </Link>
    );
}

function WhatWeDoCtaBlock() {
    const pc = usePageContent("what-we-do");
    const headline = pc?.footer_headline || "Ready to Build, Scale, or Automate Your Enterprise Software?";
    const body = pc?.footer_body || "Partner with senior RKS Systems engineers specialized in Product Engineering, AI & Data Solutions, Cloud Modernization, and Intelligent Automation. Get direct access to technical leadership with zero sales pitch.";
    const primaryLabel = pc?.footer_primary_label || "Schedule a 30-Min Architecture Session";
    const primaryTo = pc?.footer_primary_url || "/schedule-meeting";
    const primarySub = pc?.footer_primary_sub || "Meet directly with an RKS Systems Principal Engineer.";
    const secondaryLabel = pc?.footer_secondary_label || "Get a Fixed-Price or T&M Quote";
    const secondaryTo = pc?.footer_secondary_url || "/request-quote";
    const secondarySub = pc?.footer_secondary_sub || "Written proposal within 24 business hours.";
    const anchorLabel = pc?.footer_anchor_label || "Interested in Accounts Payable Automation? See InvoiceOS in Action →";
    const anchorTo = pc?.footer_anchor_url || "/products/prod-invoiceos";
    return (
        <section className="relative overflow-hidden bg-[hsl(222_47%_7%)] text-white" data-testid="what-we-do-cta">
            <div className="absolute -right-16 -top-16 h-96 w-96 opacity-10">
                <StarMotif className="h-full w-full text-primary" label="RKS" labelColor="currentColor" />
            </div>
            <div className="absolute inset-0 grid-lines opacity-40" />
            <div className="container-app relative py-20 lg:py-24">
                <span className="overline text-primary/90">Next step</span>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] max-w-3xl">{headline}</h2>
                <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl">{body}</p>
                <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
                    <div className="rounded-xl border border-primary/40 bg-white/[0.03] p-5 flex flex-col gap-3">
                        <Link to={primaryTo} data-testid="wwd-cta-schedule"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white hover:bg-primary/90 transition-colors">
                            {primaryLabel} <ArrowRight className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                        </Link>
                        <p className="text-xs text-white/50 text-center">{primarySub}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex flex-col gap-3">
                        <Link to={secondaryTo} data-testid="wwd-cta-quote"
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 font-semibold text-white hover:bg-white/10 hover:border-white/40 transition-colors">
                            {secondaryLabel} <ArrowRight className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                        </Link>
                        <p className="text-xs text-white/50 text-center">{secondarySub}</p>
                    </div>
                </div>
                <div className="mt-8">
                    <Link to={anchorTo} data-testid="wwd-cta-invoiceos"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                        <Zap className="h-4 w-4 flex-shrink-0" />{anchorLabel}
                    </Link>
                </div>
            </div>
        </section>
    );
}

// Per-service hero & footer CTA overrides
const SERVICE_CTAS = {
    "product-engineering": {
        hero: {
            primaryLabel: "Schedule a Product Architecture Review",
            primaryTo: "/schedule-meeting",
            primarySub: "30-min conversation directly with a Principal Product Engineer.",
            secondaryLabel: "Calculate Product Team Cost",
            secondaryTo: "/cost-explorer",
            secondarySub: "Instant estimate based on team pod size and tech stack.",
        },
        footer: {
            headline: "Ready to Accelerate Your Digital Product Roadmap?",
            body: "Partner with senior RKS Systems engineers to build, modernize, or scale your software products with zero sales friction and complete delivery accountability.",
            primaryLabel: "Schedule a 30-Min Technical Architecture Session",
            primaryTo: "/schedule-meeting",
            primarySub: "Speak directly with an RKS Principal Product Architect.",
            secondaryLabel: "Request a Fixed-Price or T&M Estimate",
            secondaryTo: "/request-quote",
            secondarySub: "Detailed scope, team structure, and pricing proposal in 24 business hours.",
            anchor: { label: "Looking for AI-powered operational tools? Explore InvoiceOS AP Automation →", to: "/products/prod-invoiceos" },
        },
    },
};

function ServiceHeroCTAs({ slug, pc }) {
    const def = SERVICE_CTAS[slug]?.hero;
    const cfg = (pc?.hero_primary_label) ? {
        primaryLabel: pc.hero_primary_label,
        primaryTo: pc.hero_primary_url || "/schedule-meeting",
        primarySub: pc.hero_primary_sub || "",
        secondaryLabel: pc.hero_secondary_label || "Request Quote",
        secondaryTo: pc.hero_secondary_url || "/request-quote",
        secondarySub: pc.hero_secondary_sub || "",
    } : def;

    if (!cfg) return (
        <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/schedule-meeting" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white btn-lift hover:bg-primary/90">Schedule Meeting <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} /></Link>
            <Link to="/request-quote" className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/[0.04] px-5 py-3 font-semibold text-white btn-lift hover:bg-white/[0.08]">Request Quote</Link>
        </div>
    );
    return (
        <div className="mt-9 grid sm:grid-cols-2 gap-4 max-w-xl">
            <div className="flex flex-col gap-2">
                <Link to={cfg.primaryTo} data-testid={`service-hero-primary-${slug}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white btn-lift hover:bg-primary/90 text-sm">
                    {cfg.primaryLabel} <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                </Link>
                {cfg.primarySub && <p className="text-xs text-white/50">{cfg.primarySub}</p>}
            </div>
            <div className="flex flex-col gap-2">
                <Link to={cfg.secondaryTo} data-testid={`service-hero-secondary-${slug}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/[0.04] px-5 py-3 font-semibold text-white btn-lift hover:bg-white/[0.08] text-sm">
                    {cfg.secondaryLabel} <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                </Link>
                {cfg.secondarySub && <p className="text-xs text-white/50">{cfg.secondarySub}</p>}
            </div>
        </div>
    );
}

function ServiceFooterCTA({ slug, title, pc }) {
    const def = SERVICE_CTAS[slug]?.footer;
    const cfg = (pc?.footer_headline) ? {
        headline: pc.footer_headline,
        body: pc.footer_body || "",
        primaryLabel: pc.footer_primary_label || "Schedule Meeting",
        primaryTo: pc.footer_primary_url || "/schedule-meeting",
        primarySub: pc.footer_primary_sub || "",
        secondaryLabel: pc.footer_secondary_label || "Request Quote",
        secondaryTo: pc.footer_secondary_url || "/request-quote",
        secondarySub: pc.footer_secondary_sub || "",
        anchor: (pc.footer_anchor_label && pc.footer_anchor_url) ? { label: pc.footer_anchor_label, to: pc.footer_anchor_url } : null,
    } : def;

    if (!cfg) return <CTABlock title={`Talk to a ${title} principal engineer.`} />;
    return (
        <section className="relative overflow-hidden bg-[hsl(222_47%_7%)] text-white" data-testid={`service-cta-${slug}`}>
            <div className="absolute -right-16 -top-16 h-96 w-96 opacity-10">
                <StarMotif className="h-full w-full text-primary" label="RKS" labelColor="currentColor" />
            </div>
            <div className="absolute inset-0 grid-lines opacity-40" />
            <div className="container-app relative py-20 lg:py-24">
                <span className="overline text-primary/90">Next step</span>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] max-w-3xl">{cfg.headline}</h2>
                <p className="mt-5 text-base text-white/70 leading-relaxed max-w-2xl">{cfg.body}</p>
                <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
                    <div className="rounded-xl border border-primary/40 bg-white/[0.03] p-5 flex flex-col gap-2.5">
                        <Link to={cfg.primaryTo} data-testid={`service-cta-${slug}-primary`}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white hover:bg-primary/90 transition-colors text-sm text-center">
                            {cfg.primaryLabel} <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                        </Link>
                        {cfg.primarySub && <p className="text-xs text-white/50 text-center">{cfg.primarySub}</p>}
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex flex-col gap-2.5">
                        <Link to={cfg.secondaryTo} data-testid={`service-cta-${slug}-secondary`}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 font-semibold text-white hover:bg-white/10 hover:border-white/40 transition-colors text-sm text-center">
                            {cfg.secondaryLabel} <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                        </Link>
                        {cfg.secondarySub && <p className="text-xs text-white/50 text-center">{cfg.secondarySub}</p>}
                    </div>
                </div>
                {cfg.anchor && (
                    <div className="mt-8">
                        <Link to={cfg.anchor.to} data-testid={`service-cta-${slug}-anchor`}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                            <Zap className="h-3.5 w-3.5 flex-shrink-0" />{cfg.anchor.label}
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}


export function ServicesIndex() {
    const pc = usePageContent("what-we-do");
    useSEO({
        title: "What We Do — Services | RKS Systems",
        description: "Enterprise Software Engineering & AI Solutions across 7 core practices. Led by principal RKS Systems engineers.",
    });

    const tier1 = SERVICE_CARDS.slice(0, 4);
    const tier2 = SERVICE_CARDS.slice(4);

    return (
        <div data-testid="page-services-index">
            {/* ── Hero ── */}
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-24 lg:py-32">
                    <div className="max-w-3xl">
                        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
                            {pc?.hero_headline || "Enterprise Software Engineering & AI Solutions Services"}
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            {pc?.hero_subheadline || "RKS Systems delivers full-lifecycle digital transformation across seven core engineering practices. Led by principal RKS Systems engineers, our accountable pods spin up in weeks to design, build, and modernize production-ready software."}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/schedule-meeting" data-testid="hero-cta-schedule"
                                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white hover:bg-primary/90 transition-colors">
                                Schedule a Tech Consultation <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                            </Link>
                            <a href="#practices" data-testid="hero-cta-practices"
                                className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/[0.04] px-5 py-3 font-semibold text-white hover:bg-white/[0.08] transition-colors">
                                Explore Our 7 Practices <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Services Grid ── */}
            <section id="practices" className="py-20 lg:py-28">
                <div className="container-app space-y-14">
                    {/* Tier 1 — Core Development & AI Engine */}
                    <div>
                        <div className="mb-7">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-primary">Featured Practices</span>
                            <h2 className="mt-1 font-heading text-lg font-bold text-foreground">Core Development &amp; AI Engine</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                            {tier1.map((s) => <ServiceCard key={s.slug} s={s} />)}
                        </div>
                    </div>

                    {/* Tier 2 — Platform & Infrastructure */}
                    <div>
                        <div className="mb-7">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-primary">Foundational Practices</span>
                            <h2 className="mt-1 font-heading text-lg font-bold text-foreground">Platform &amp; Infrastructure</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {tier2.map((s) => <ServiceCard key={s.slug} s={s} />)}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Custom CTA ── */}
            <WhatWeDoCtaBlock />
        </div>
    );
}

export function ServiceDetail() {
    const { slug } = useParams();
    const { item: s, notFound } = useServiceBySlug(slug);
    const pc = usePageContent(s ? `service-${s.slug}` : null);
    useSEO({
        title: s ? `${s.title} — RKS Systems` : "Service — RKS Systems",
        description: s?.tagline,
        jsonLd: s ? {
            "@context": "https://schema.org",
            "@type": "Service",
            name: s.title,
            description: s.overview,
            provider: { "@type": "Organization", name: "RKS Systems" },
        } : undefined,
    });
    if (notFound) return <Navigate to="/what-we-do" replace />;
    if (!s) return <div className="container-app py-24 text-center text-muted-foreground">Loading...</div>;

    // page_content overrides: headline and subheadline can be set via Admin → Page Banners
    const heroHeadline = pc?.hero_headline || s.tagline || s.title;
    const heroSubheadline = pc?.hero_subheadline || s.overview;

    return (
        <div data-testid={`page-service-${s.slug}`}>
            {/* Hero */}
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-16 lg:py-24">
                    <Link to="/what-we-do" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8"><ArrowLeft className="h-3.5 w-3.5" /> All services</Link>
                    <div className="grid lg:grid-cols-[1.6fr,1fr] gap-12 items-center">
                        <div>
                            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">{heroHeadline}</h1>
                            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">{heroSubheadline}</p>
                            <ServiceHeroCTAs slug={s.slug} pc={pc} />
                        </div>
                        {s.image && (
                            <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10">
                                <img src={absUrl(s.image)} alt={s.title} className="h-full w-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222_47%_7%)]/60 to-transparent" />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Body */}
            <section className="py-16 lg:py-20">
                <div className="container-app grid lg:grid-cols-[280px,1fr] gap-12">
                    <aside className="lg:sticky lg:top-24 self-start hidden lg:block">
                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">On this page</div>
                        <ul className="text-sm space-y-2">
                            <li><a href="#challenges" className="text-foreground/80 hover:text-primary">Business challenges</a></li>
                            <li><a href="#approach" className="text-foreground/80 hover:text-primary">RKS approach</a></li>
                            <li><a href="#capabilities" className="text-foreground/80 hover:text-primary">Capabilities</a></li>
                            <li><a href="#stack" className="text-foreground/80 hover:text-primary">Technology stack</a></li>
                            <li><a href="#governance" className="text-foreground/80 hover:text-primary">Delivery governance</a></li>
                            <li><a href="#engagement" className="text-foreground/80 hover:text-primary">Engagement models</a></li>
                            <li><a href="#faq" className="text-foreground/80 hover:text-primary">FAQs</a></li>
                        </ul>
                    </aside>

                    <div className="max-w-3xl space-y-14">
                        {s.challenges?.length ? (
                            <div id="challenges">
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold">Business challenges</h2>
                                <ul className="mt-5 space-y-3">
                                    {s.challenges.map((c) => (
                                        <li key={c} className="flex items-start gap-3 text-base">
                                            <ChevronRight className="h-4 w-4 mt-1.5 text-primary flex-shrink-0" strokeWidth={2} />
                                            <span className="text-foreground/85">{c}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}

                        {s.approach ? (
                            <div id="approach">
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold">The RKS approach</h2>
                                <p className="mt-4 text-base text-foreground/80 leading-relaxed">{s.approach}</p>
                            </div>
                        ) : null}

                        {s.capabilities?.length ? (
                            <div id="capabilities">
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold">Capabilities</h2>
                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {s.capabilities.map((c) => (
                                        <div key={c} className="flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3">
                                            <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" strokeWidth={1.75} />
                                            <span className="text-sm">{c}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {s.stack?.length ? (
                            <div id="stack">
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold">Technology stack</h2>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {s.stack.map((t) => (
                                        <span key={t} className="inline-flex items-center rounded-full border border-border bg-secondary/60 px-3.5 py-1.5 text-xs font-semibold text-secondary-foreground">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {s.governance ? (
                            <div id="governance">
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold">Delivery governance</h2>
                                <p className="mt-4 text-base text-foreground/80 leading-relaxed">{s.governance}</p>
                            </div>
                        ) : null}

                        {s.engagements?.length ? (
                            <div id="engagement">
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold">Engagement models</h2>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {s.engagements.map((e) => (
                                        <span key={e} className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary">
                                            {e}
                                        </span>
                                    ))}
                                </div>
                                <Link to="/engagement-models" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                                    Compare engagement models <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                </Link>
                            </div>
                        ) : null}

                        {s.faqs?.length ? (
                            <div id="faq">
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold">FAQs</h2>
                                <div className="mt-5 divide-y divide-border rounded-xl border border-border overflow-hidden">
                                    {s.faqs.map((f, i) => (
                                        <details key={i} data-testid={`faq-${s.slug}-${i}`} className="group open:bg-secondary/30">
                                            <summary className="cursor-pointer list-none px-5 py-4 flex items-start justify-between gap-4 text-base font-semibold hover:bg-secondary/30">
                                                <span>{f.q}</span>
                                                <ChevronRight className="h-4 w-4 mt-1 flex-shrink-0 text-muted-foreground transition-transform group-open:rotate-90" strokeWidth={2} />
                                            </summary>
                                            <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
                                        </details>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </section>
            <ServiceFooterCTA slug={s.slug} title={s.title} pc={pc} />
        </div>
    );
}
